import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../atoms/button/button.component';
import { IconComponent } from '../../atoms/icon/icon.component';

export type GradientColor = 'blue' | 'green' | 'purple' | 'red' | 'orange' | 'indigo' | 'white';
export type ComponentSize = 'sm' | 'md' | 'lg';

export interface ActionButton {
  label: string;
  icon?: string;
  variant?:
    | 'primary'
    | 'secondary'
    | 'white'
    | 'white-blue'
    | 'white-green'
    | 'white-purple'
    | 'white-red'
    | 'white-orange'
    | 'white-indigo'
    | 'gradient'
    | 'danger';
  size?: ComponentSize; // Optional per-button size override
  action: () => void;
}

/**
 * PageHeader Component - Reusable page header with gradient backgrounds, icons, and multiple action buttons
 *
 * @example
 * <app-molecule-page-header
 *   title="Users Management"
 *   description="Manage all users in the system"
 *   icon="users"
 *   gradient="blue"
 *   [actions]="headerActions">
 * </app-molecule-page-header>
 */
@Component({
  selector: 'app-molecule-page-header',
  standalone: true,
  imports: [CommonModule, ButtonComponent, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
})
export class PageHeaderComponent {
  // Basic content
  title = input.required<string>();
  description = input<string>('');

  // Icon badge
  icon = input<string>(''); // Icon name without 'ti-' prefix (e.g., 'users', 'info')
  iconBgColor = input<string>('bg-white/20'); // Icon badge background

  // Gradient configuration
  gradient = input<GradientColor>('blue');
  customGradient = input<string>(''); // Override with custom gradient classes

  // Sizing
  size = input<ComponentSize>('md'); // Header padding/height size
  buttonSize = input<ComponentSize>('md'); // Default button size

  // Action buttons (supports multiple buttons)
  actions = input<ActionButton[]>([]);

  // Back button
  showBackButton = input<boolean>(false);
  backAction = output<void>();

  /**
   * Get header padding classes based on size
   */
  getHeaderPaddingClasses(): string {
    const paddingMap: Record<ComponentSize, string> = {
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    };
    return paddingMap[this.size()];
  }

  /**
   * Get icon size classes based on header size
   */
  getIconSizeClasses(): string {
    const iconMap: Record<ComponentSize, string> = {
      sm: 'p-2',
      md: 'p-3',
      lg: 'p-4',
    };
    return iconMap[this.size()];
  }

  /**
   * Get icon component size based on header size
   */
  getIconSize(): 'md' | 'lg' | 'xl' {
    const sizeMap: Record<ComponentSize, 'md' | 'lg' | 'xl'> = {
      sm: 'md',
      md: 'lg',
      lg: 'xl',
    };
    return sizeMap[this.size()];
  }

  /**
   * Get title text size classes based on header size
   */
  getTitleSizeClasses(): string {
    const titleMap: Record<ComponentSize, string> = {
      sm: 'text-xl',
      md: 'text-2xl',
      lg: 'text-3xl',
    };
    return titleMap[this.size()];
  }

  /**
   * Get description text size classes based on header size
   */
  getDescriptionSizeClasses(): string {
    const descriptionMap: Record<ComponentSize, string> = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };
    return descriptionMap[this.size()];
  }

  /**
   * Get button size for an action (with per-button override)
   */
  getButtonSize(action: ActionButton): ComponentSize {
    return action.size || this.buttonSize();
  }

  /**
   * Get gradient classes based on color
   */
  getGradientClasses(): string {
    if (this.customGradient()) {
      return this.customGradient();
    }

    const gradients: Record<GradientColor, string> = {
      blue: 'from-blue-500 via-indigo-500 to-purple-600',
      green: 'from-green-500 via-emerald-500 to-teal-600',
      purple: 'from-purple-500 via-violet-500 to-indigo-600',
      red: 'from-red-500 via-rose-500 to-pink-600',
      orange: 'from-orange-500 via-amber-500 to-yellow-600',
      indigo: 'from-indigo-500 via-blue-500 to-cyan-600',
      white: 'from-white to-gray-50',
    };

    return gradients[this.gradient()];
  }

  /**
   * Check if the gradient is a light theme (white)
   */
  isLightTheme(): boolean {
    return this.gradient() === 'white';
  }

  /**
   * Get text color classes based on theme
   */
  getTextColorClasses(): string {
    return this.isLightTheme() ? 'text-gray-900' : 'text-white';
  }

  /**
   * Get description text color classes based on theme
   */
  getDescriptionColorClasses(): string {
    return this.isLightTheme() ? 'text-gray-600' : 'text-white/90';
  }

  /**
   * Get icon background for light theme
   */
  getIconBgClasses(): string {
    if (this.isLightTheme()) {
      return 'bg-blue-50';
    }
    return this.iconBgColor();
  }

  /**
   * Get icon color classes
   */
  getIconColorClasses(): string {
    return this.isLightTheme() ? 'text-blue-600' : 'text-white';
  }

  /**
   * Get the white button variant that matches the header gradient
   * Used for buttons inside the header to have gradient text
   */
  getWhiteButtonVariant():
    | 'white-blue'
    | 'white-green'
    | 'white-purple'
    | 'white-red'
    | 'white-orange'
    | 'white-indigo'
    | 'secondary' {
    if (this.isLightTheme()) {
      return 'secondary';
    }
    const variantMap: Record<
      Exclude<GradientColor, 'white'>,
      'white-blue' | 'white-green' | 'white-purple' | 'white-red' | 'white-orange' | 'white-indigo'
    > = {
      blue: 'white-blue',
      green: 'white-green',
      purple: 'white-purple',
      red: 'white-red',
      orange: 'white-orange',
      indigo: 'white-indigo',
    };
    return variantMap[this.gradient() as Exclude<GradientColor, 'white'>];
  }

  /**
   * Handle back button click
   */
  onBackClick(): void {
    this.backAction.emit();
  }

  /**
   * Execute action button callback
   */
  executeAction(action: () => void): void {
    action();
  }
}
